/**
 * /expenses
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Receipt } from 'lucide-react'
import { RowActions } from '@/components/row-actions'
import { PageHeader } from '@/components/app-shell'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Field } from '@/components/ui/field'
import { KpiCard } from '@/components/ui/kpi-card'
import {
  ApiError,
  createExpense,
  deleteExpense,
  patchExpense,
  listExpenseCategories,
  listExpenses,
  listVehicles,
  listVendors,
} from '@/lib/api'

export function ExpensesPage() {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [vehicleId, setVehicleId] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [vendorId, setVendorId] = useState('')

  const listQ = useQuery({ queryKey: ['expenses'], queryFn: () => listExpenses() })
  const catsQ = useQuery({ queryKey: ['expense-categories'], queryFn: listExpenseCategories })
  const vehiclesQ = useQuery({ queryKey: ['vehicles'], queryFn: () => listVehicles() })
  const vendorsQ = useQuery({ queryKey: ['vendors'], queryFn: () => listVendors() })

  const mutation = useMutation({
    mutationFn: createExpense,
    onSuccess: async () => {
      setOpen(false)
      setAmount('')
      setDescription('')
      await qc.invalidateQueries({ queryKey: ['expenses'] })
    },
  })

  const rows = listQ.data?.expenses ?? []
  const total = listQ.data?.total ?? 0

  return (
    <>
      <PageHeader
        title="Expenses"
        subtitle="Combustible, peajes y seguros"
        actions={
          <Button type="button" onClick={() => setOpen((v) => !v)}>
            {open ? 'Cerrar' : 'Add expense'}
          </Button>
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3">
        <KpiCard label="Registros" value={rows.length} />
        <KpiCard label="Total" value={`$${total.toLocaleString()}`} />
      </div>

      {open && (
        <Card className="mb-4">
          <CardHeader title="Nuevo gasto" />
          <CardBody className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Importe *"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
            <Field
              label="Descripción"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium">Vehículo</span>
              <select
                className="h-11 rounded-lg border border-input bg-card px-3"
                value={vehicleId}
                onChange={(e) => setVehicleId(e.target.value)}
              >
                <option value="">—</option>
                {(vehiclesQ.data?.vehicles ?? []).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.unitNumber}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium">Categoría</span>
              <select
                className="h-11 rounded-lg border border-input bg-card px-3"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">—</option>
                {(catsQ.data?.categories ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="font-medium">Proveedor</span>
              <select
                className="h-11 rounded-lg border border-input bg-card px-3"
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value)}
              >
                <option value="">—</option>
                {(vendorsQ.data?.vendors ?? []).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.companyName}
                  </option>
                ))}
              </select>
            </label>
            <Button
              type="button"
              className="sm:col-span-2"
              loading={mutation.isPending}
              onClick={() =>
                mutation.mutate({
                  amount: Number(amount),
                  description: description || null,
                  vehicleId: vehicleId || null,
                  categoryId: categoryId || null,
                  vendorId: vendorId || null,
                })
              }
            >
              Guardar
            </Button>
          </CardBody>
        </Card>
      )}

      {listQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState icon={Receipt} title="Sin gastos" description="Añade el primero arriba." />
        </Card>
      ) : (
        <Card className="divide-y divide-border overflow-hidden">
          {rows.map((e) => (
            <ExpenseRow key={e.id} expense={e} />
          ))}
        </Card>
      )}
    </>
  )
}

/**
 * Un gasto, corregible en el sitio.
 *
 * Importe y fecha son los que se teclean mal, y no eran editables: un cero de
 * más quedaba fijo en el informe de coste por vehículo (v_vehicle_cost), que es
 * con lo que se decide si una unidad sale rentable o se retira.
 */
function ExpenseRow({
  expense,
}: {
  expense: {
    id: string
    date: string
    amount: number
    description: string | null
    categoryName?: string | null
    unitNumber?: string | null
    vendorName?: string | null
  }
}) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [amount, setAmount] = useState(String(expense.amount))
  const [date, setDate] = useState(expense.date)
  const [description, setDescription] = useState(expense.description ?? '')
  const [error, setError] = useState<string | null>(null)

  const refresh = () => qc.invalidateQueries({ queryKey: ['expenses'] })

  const save = useMutation({
    mutationFn: () =>
      patchExpense(expense.id, {
        amount: Number(amount),
        date,
        description: description || null,
      }),
    onSuccess: async () => {
      setEditing(false)
      setError(null)
      await refresh()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo guardar.'),
  })

  const remove = useMutation({
    mutationFn: () => deleteExpense(expense.id),
    onSuccess: refresh,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo eliminar.'),
  })

  if (editing) {
    return (
      <div className="space-y-2 px-4 py-3">
        <div className="grid gap-2 sm:grid-cols-3">
          <Field
            label="Importe"
            type="number"
            inputMode="decimal"
            value={amount}
            onChange={(ev) => setAmount(ev.target.value)}
          />
          <Field label="Fecha" type="date" value={date} onChange={(ev) => setDate(ev.target.value)} />
          <Field
            label="Descripción"
            value={description}
            onChange={(ev) => setDescription(ev.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Button type="button" className="flex-1" loading={save.isPending} onClick={() => save.mutate()}>
            Guardar
          </Button>
          <Button type="button" variant="outline" onClick={() => setEditing(false)}>
            Cancelar
          </Button>
        </div>
        {error && (
          <p role="alert" className="text-xs font-medium text-danger">
            {error}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="px-4 py-3">
      <div className="flex min-h-[56px] items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">
            {expense.description || expense.categoryName || 'Gasto'}
          </p>
          <p className="text-xs text-muted-foreground">
            {expense.date}
            {expense.unitNumber ? ` · Unit ${expense.unitNumber}` : ''}
            {expense.vendorName ? ` · ${expense.vendorName}` : ''}
          </p>
        </div>
        <span className="flex items-center gap-2">
          <span className="font-medium">${expense.amount.toLocaleString()}</span>
          <RowActions
            label={`el gasto del ${expense.date}`}
            deleting={remove.isPending}
            onEdit={() => setEditing(true)}
            onDelete={() => remove.mutate()}
          />
        </span>
      </div>
      {error && (
        <p role="alert" className="mt-1 text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  )
}
